import type { FC, FormEvent } from 'react';
import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  OutlinedInput,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Unstable_Grid2 as Grid,
  CircularProgress
} from '@mui/material';
import { leadsApi } from 'src/api/supabase';

interface FormData {
  name: string;
  email: string;
  phone: string;
  propertyType: string;
  address: string;
  timeline: string;
  budget: string;
  projectDetails: string;
  newsletter: boolean;
}

export const ContactForm: FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseAddress = (fullAddress: string) => {
    // Try to parse "123 Main Street, City, State, ZIP" format
    const parts = fullAddress.split(',').map(p => p.trim());

    if (parts.length >= 3) {
      const address = parts[0];
      const city = parts[1];
      // Last part might be "State ZIP" or just "State"
      const stateZipPart = parts[parts.length - 1];
      const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s*(\d{5})?$/i);

      if (stateZipMatch) {
        return {
          address,
          city,
          state: stateZipMatch[1].toUpperCase(),
          zipCode: stateZipMatch[2] || ''
        };
      }

      return {
        address,
        city,
        state: stateZipPart,
        zipCode: ''
      };
    }

    // Fallback - just use the whole thing as address
    return {
      address: fullAddress,
      city: '',
      state: 'WA',
      zipCode: ''
    };
  };

  const parseName = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      setIsSubmitting(true);
      setError(null);

      const form = event.currentTarget;
      const formData = new FormData(form);

      const data: FormData = {
        name: formData.get('name') as string || '',
        email: formData.get('email') as string || '',
        phone: formData.get('phone') as string || '',
        propertyType: formData.get('propertyType') as string || '',
        address: formData.get('address') as string || '',
        timeline: formData.get('timeline') as string || '',
        budget: formData.get('budget') as string || '',
        projectDetails: formData.get('projectDetails') as string || '',
        newsletter: formData.get('newsletter') === 'on',
      };

      try {
        // Parse name and address
        const { firstName, lastName } = parseName(data.name);
        const addressParts = parseAddress(data.address);

        // Build project notes with all form details
        const projectNotes = [
          `Property Type: ${data.propertyType}`,
          `Timeline: ${data.timeline}`,
          `Budget: ${data.budget || 'Not specified'}`,
          data.projectDetails ? `Details: ${data.projectDetails}` : '',
          `Newsletter: ${data.newsletter ? 'Yes' : 'No'}`,
          `Source: Website Contact Form`,
          `Submitted: ${new Date().toLocaleString()}`
        ].filter(Boolean).join('\n');

        // Create lead in Supabase
        // Note: projectType from form (residential, commercial, etc.) doesn't match DB enum
        // So we store property type info in projectNotes instead
        await leadsApi.create({
          firstName,
          lastName,
          email: data.email,
          phone: data.phone,
          address1: addressParts.address,
          city: addressParts.city,
          state: addressParts.state,
          zipCode: addressParts.zipCode,
          projectAddress: data.address,
          projectNotes,
          leadStatus: 'facebook_new',
          leadSource: 'website' as any,
          acceptsMarketing: data.newsletter,
        });

        // Also submit to Formspree as backup
        await fetch('https://formspree.io/f/xldppbno', {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/json',
          },
        });

        setSubmitted(true);
      } catch (err) {
        console.error('Error submitting form:', err);
        setError('There was an error submitting your request. Please try again.');

        // Still try Formspree even if Supabase fails
        try {
          await fetch('https://formspree.io/f/xldppbno', {
            method: 'POST',
            body: formData,
            headers: {
              Accept: 'application/json',
            },
          });
          setSubmitted(true);
          setError(null);
        } catch {
          // Both failed
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h4" sx={{ mb: 2, color: 'success.main' }}>
          Thank you for your inquiry!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Our team will respond with a customized quote within 24 hours.
        </Typography>
      </Box>
    );
  }

  const inputStyles = {
    '& .MuiOutlinedInput-input': {
      color: '#0a0a0b',
      fontSize: { xs: '16px', sm: '14px' }
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: '#a1a1aa',
      opacity: 1
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: { xs: 2, sm: 1 },
      backgroundColor: '#fafafa',
      '& fieldset': { borderColor: '#e4e4e7' },
      '&:hover fieldset': { borderColor: '#a1a1aa' },
      '&.Mui-focused fieldset': { borderColor: '#2563eb' }
    }
  };

  const labelStyles = {
    color: '#0a0a0b',
    mb: 1,
    fontWeight: 500,
    fontSize: { xs: '0.9rem', sm: '0.875rem' }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyles}>
              Full Name *
            </FormLabel>
            <OutlinedInput
              name="name"
              required
              placeholder="John Doe"
              sx={inputStyles}
            />
          </FormControl>
        </Grid>
        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyles}>
              Email Address *
            </FormLabel>
            <OutlinedInput
              name="email"
              type="email"
              required
              placeholder="john@example.com"
              sx={inputStyles}
            />
          </FormControl>
        </Grid>
        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyles}>
              Phone Number *
            </FormLabel>
            <OutlinedInput
              name="phone"
              required
              type="tel"
              placeholder="(555) 123-4567"
              sx={inputStyles}
            />
          </FormControl>
        </Grid>
        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyles}>
              Property Type *
            </FormLabel>
            <Select
              name="propertyType"
              required
              defaultValue=""
              sx={{
                borderRadius: { xs: 2, sm: 1 },
                backgroundColor: '#fafafa',
                '& .MuiSelect-select': {
                  color: '#0a0a0b',
                  fontSize: { xs: '16px', sm: '14px' }
                },
                '& fieldset': { borderColor: '#e4e4e7' },
                '&:hover fieldset': { borderColor: '#a1a1aa' },
                '&.Mui-focused fieldset': { borderColor: '#2563eb' }
              }}
            >
              <MenuItem value="residential">Residential</MenuItem>
              <MenuItem value="commercial">Commercial</MenuItem>
              <MenuItem value="retail">Retail</MenuItem>
              <MenuItem value="industrial">Industrial</MenuItem>
              <MenuItem value="municipal">Municipal/Government</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyles}>
              Project Address *
            </FormLabel>
            <OutlinedInput
              name="address"
              required
              placeholder="123 Main Street, City, State, ZIP"
              sx={inputStyles}
            />
          </FormControl>
        </Grid>
        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyles}>
              Project Timeline
            </FormLabel>
            <Select
              name="timeline"
              defaultValue="1-3months"
              sx={{
                borderRadius: { xs: 2, sm: 1 },
                backgroundColor: '#fafafa',
                '& .MuiSelect-select': {
                  color: '#0a0a0b',
                  fontSize: { xs: '16px', sm: '14px' }
                },
                '& fieldset': { borderColor: '#e4e4e7' },
                '&:hover fieldset': { borderColor: '#a1a1aa' },
                '&.Mui-focused fieldset': { borderColor: '#2563eb' }
              }}
            >
              <MenuItem value="asap">As Soon As Possible</MenuItem>
              <MenuItem value="1month">Within 1 Month</MenuItem>
              <MenuItem value="1-3months">1-3 Months</MenuItem>
              <MenuItem value="3-6months">3-6 Months</MenuItem>
              <MenuItem value="planning">Just Planning</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyles}>
              Estimated Budget Range
            </FormLabel>
            <Select
              name="budget"
              defaultValue=""
              sx={{
                borderRadius: { xs: 2, sm: 1 },
                backgroundColor: '#fafafa',
                '& .MuiSelect-select': {
                  color: '#0a0a0b',
                  fontSize: { xs: '16px', sm: '14px' }
                },
                '& fieldset': { borderColor: '#e4e4e7' },
                '&:hover fieldset': { borderColor: '#a1a1aa' },
                '&.Mui-focused fieldset': { borderColor: '#2563eb' }
              }}
            >
              <MenuItem value="">Prefer not to say</MenuItem>
              <MenuItem value="under-5k">Under $5,000</MenuItem>
              <MenuItem value="5k-10k">$5,000 - $10,000</MenuItem>
              <MenuItem value="10k-25k">$10,000 - $25,000</MenuItem>
              <MenuItem value="25k-50k">$25,000 - $50,000</MenuItem>
              <MenuItem value="over-50k">Over $50,000</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyles}>
              Project Details & Special Requirements
            </FormLabel>
            <OutlinedInput
              name="projectDetails"
              placeholder="Tell us about your vision, specific areas you want to light, any special features or requirements..."
              multiline
              minRows={3}
              sx={inputStyles}
            />
          </FormControl>
        </Grid>
        <Grid xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="newsletter"
                defaultChecked
                sx={{ '& .MuiSvgIcon-root': { fontSize: { xs: 24, sm: 20 } } }}
              />
            }
            label={
              <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' }, color: 'rgba(255,255,255,0.7)' }}>
                I'd like to receive updates about new products and promotions
              </Typography>
            }
          />
        </Grid>
      </Grid>

      {error && (
        <Typography color="error" sx={{ mt: 2, textAlign: 'center', fontSize: '0.875rem' }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2.5, sm: 3 } }}>
        <Button
          fullWidth
          size="large"
          variant="contained"
          type="submit"
          disabled={isSubmitting}
          sx={{
            py: { xs: 1.75, sm: 1.5 },
            fontSize: { xs: '1rem', sm: '0.9375rem' },
            fontWeight: 600,
            borderRadius: { xs: 2, sm: 1 }
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Get My Free Quote'
          )}
        </Button>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        sx={{
          display: 'block',
          mt: 2,
          fontSize: { xs: '0.75rem', sm: '0.75rem' },
          px: { xs: 1, sm: 0 }
        }}
      >
        * Required fields. We respect your privacy and will never share your information.
      </Typography>
    </form>
  );
};
